import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AICoachChat } from '../components/AICoachChat';
import { FeatureGate } from '../components/FeatureGate';
import { SetupFeeGate } from '../components/SetupFeeGate';
import { CoachingChatService } from '@shared/services/coachingChatService';
import type { CoachingConversation } from '@shared/types/coaching-chat.types';
import { MessageCircle, Clock, CheckCircle, Sparkles, Plus, Trash2, Target, Briefcase, FileText, TrendingUp, Heart, Users, ArrowUp, ChevronDown } from 'lucide-react';
import { Button } from '@shared/components/ui/Button';
import { ConfirmDialog } from '@shared/components/ui/ConfirmDialog';
import { toast } from '@shared/components/ui/Toaster';
import { FEATURE_GATES } from '../services/subscriptionService';

const AICoach: React.FC = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<CoachingConversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [showChat, setShowChat] = useState(false);
  const [quota, setQuota] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; conversationId: string | null }>({ isOpen: false, conversationId: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [hoveredConversation, setHoveredConversation] = useState<string | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isScrolledTop, setIsScrolledTop] = useState(true);
  const [isScrolledBottom, setIsScrolledBottom] = useState(false);
  const conversationsListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const [conversationsData, quotaData] = await Promise.all([
        CoachingChatService.getUserConversations(user.id),
        CoachingChatService.getUserQuota(user.id)
      ]);

      setConversations(conversationsData);
      setQuota(quotaData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewConversation = () => {
    setSelectedConversation(null);
    setShowChat(true);
  };

  const handleSelectConversation = (conversationId: string) => {
    setSelectedConversation(conversationId);
    setShowChat(true);
  };

  const handleCloseChat = () => {
    setShowChat(false);
    setSelectedConversation(null);
    loadData();
  };

  const handleDeleteClick = (e: React.MouseEvent, conversationId: string) => {
    e.stopPropagation();
    setDeleteConfirm({ isOpen: true, conversationId });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.conversationId) return;

    try {
      setIsDeleting(true);
      await CoachingChatService.deleteConversation(deleteConfirm.conversationId);

      toast.success('Conversation deleted successfully');

      if (selectedConversation === deleteConfirm.conversationId) {
        setShowChat(false);
        setSelectedConversation(null);
      }

      await loadData();
    } catch (error) {
      console.error('Error deleting conversation:', error);
      toast.error('Failed to delete conversation. Please try again.');
    } finally {
      setIsDeleting(false);
      setDeleteConfirm({ isOpen: false, conversationId: null });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ isOpen: false, conversationId: null });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getGoalLabel = (goal: string | null) => {
    const labels: Record<string, string> = {
      skills_development: 'Skills Development',
      interview_prep: 'Interview Prep',
      offer_analysis: 'Offer Analysis',
      decision_making: 'Decision Making',
      motivation: 'Motivation',
      career_planning: 'Career Planning',
      general: 'General Guidance'
    };
    return labels[goal || 'general'] || 'General Guidance';
  };

  const getGoalIcon = (goal: string | null) => {
    const icons: Record<string, React.ReactNode> = {
      skills_development: <Target className="w-5 h-5" />,
      interview_prep: <Briefcase className="w-5 h-5" />,
      offer_analysis: <FileText className="w-5 h-5" />,
      decision_making: <TrendingUp className="w-5 h-5" />,
      motivation: <Heart className="w-5 h-5" />,
      career_planning: <Users className="w-5 h-5" />,
      general: <MessageCircle className="w-5 h-5" />
    };
    return icons[goal || 'general'] || <MessageCircle className="w-5 h-5" />;
  };

  const getGoalColor = (goal: string | null) => {
    const colors: Record<string, string> = {
      skills_development: 'border-l-blue-500 bg-blue-50/50',
      interview_prep: 'border-l-green-500 bg-green-50/50',
      offer_analysis: 'border-l-amber-500 bg-amber-50/50',
      decision_making: 'border-l-purple-500 bg-purple-50/50',
      motivation: 'border-l-pink-500 bg-pink-50/50',
      career_planning: 'border-l-teal-500 bg-teal-50/50',
      general: 'border-l-gray-500 bg-gray-50/50'
    };
    return colors[goal || 'general'] || 'border-l-gray-500 bg-gray-50/50';
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    setIsScrolledTop(scrollTop === 0);
    setIsScrolledBottom(scrollTop + clientHeight >= scrollHeight - 10);
    setShowScrollTop(scrollTop > 200);
  };

  const scrollToTop = () => {
    conversationsListRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading AI Coach...</p>
        </div>
      </div>
    );
  }

  return (
    <SetupFeeGate featureName="AI Career Coach">
      <FeatureGate
        featureKey={FEATURE_GATES.AI_COACH}
        featureName="AI Career Coach"
        showUpgradePrompt={true}
      >
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full overflow-hidden border-4 border-blue-100 flex-shrink-0">
                <img
                  src="https://images.pexels.com/photos/1181690/pexels-photo-1181690.jpeg?auto=compress&cs=tinysrgb&w=160&h=160&fit=crop"
                  alt="Marta - AI Career Coach"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Meet Marta</h1>
                <p className="text-gray-600">Your personal AI Career Coach with 15+ years of expertise</p>
              </div>
            </div>
            <Button onClick={handleNewConversation} className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              New Conversation
            </Button>
          </div>

          {/* Usage Stats */}
          {quota && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Messages Remaining</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {quota.messages_remaining} <span className="text-sm text-gray-500">/ {quota.messages_remaining + (quota.monthly_message_limit - quota.messages_remaining)}</span>
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Period Ends</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {new Date(quota.period_end).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Plan</p>
                  <p className="text-lg font-semibold text-gray-900 capitalize">{quota.tier}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Voice Features</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {quota.voice_enabled ? 'Enabled' : 'Disabled'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Conversations List */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Enhanced Header with Gradient */}
          <div className="bg-gradient-to-r from-blue-600 via-blue-500 to-teal-500 p-6 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Your Conversations</h2>
                  <p className="text-sm text-blue-100">
                    {conversations.length === 0 ? 'No conversations yet' : `${conversations.length} conversation${conversations.length !== 1 ? 's' : ''}`}
                  </p>
                </div>
              </div>
              {conversations.length > 0 && (
                <div className="flex items-center gap-2 text-white/80 text-sm">
                  <ChevronDown className="w-4 h-4 animate-bounce" />
                  <span className="hidden sm:inline">Scroll to view all</span>
                </div>
              )}
            </div>
          </div>

          {conversations.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <MessageCircle className="w-10 h-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                No conversations yet
              </h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Start your first conversation with Marta, your AI Career Coach, and get personalized guidance for your career journey
              </p>
              <Button onClick={handleNewConversation} className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700">
                <Sparkles className="w-5 h-5 mr-2" />
                Start Coaching Session
              </Button>
            </div>
          ) : (
            <div className="relative">
              {/* Top Shadow Indicator */}
              {!isScrolledTop && (
                <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-gray-900/10 to-transparent pointer-events-none z-10"></div>
              )}

              {/* Scrollable Conversations List */}
              <div
                ref={conversationsListRef}
                onScroll={handleScroll}
                className="max-h-[520px] overflow-y-auto scroll-smooth"
                style={{ scrollbarWidth: 'thin' }}
              >
                <div className="p-4 space-y-3">
                  {conversations.map((conversation) => (
                    <div
                      key={conversation.id}
                      onClick={() => handleSelectConversation(conversation.id)}
                      onMouseEnter={() => setHoveredConversation(conversation.id)}
                      onMouseLeave={() => setHoveredConversation(null)}
                      className={`
                        group relative bg-white border-l-4 ${getGoalColor(conversation.coaching_goal)}
                        rounded-lg shadow-sm hover:shadow-md
                        cursor-pointer transition-all duration-200 overflow-hidden
                        ${hoveredConversation === conversation.id ? 'transform scale-[1.02]' : ''}
                      `}
                    >
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-blue-50 to-teal-50 flex items-center justify-center text-blue-600">
                                {getGoalIcon(conversation.coaching_goal)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-bold text-gray-900 truncate text-lg">
                                  {conversation.title || getGoalLabel(conversation.coaching_goal)}
                                </h3>
                                <span className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full ${getStatusColor(conversation.status)}`}>
                                  {conversation.status === 'active' && <span className="w-1.5 h-1.5 bg-green-600 rounded-full mr-1.5 animate-pulse"></span>}
                                  {conversation.status}
                                </span>
                              </div>
                            </div>

                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              <span className="flex items-center gap-1.5 font-medium">
                                <MessageCircle className="w-4 h-4 text-blue-500" />
                                <span className="text-gray-900">{conversation.total_messages}</span> messages
                              </span>
                              <span className="flex items-center gap-1.5">
                                <Clock className="w-4 h-4 text-gray-400" />
                                {new Date(conversation.updated_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 flex-shrink-0">
                            {conversation.status === 'completed' && (
                              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                              </div>
                            )}
                            {hoveredConversation === conversation.id && (
                              <button
                                onClick={(e) => handleDeleteClick(e, conversation.id)}
                                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-red-100 text-gray-400 hover:text-red-600 transition-all duration-200"
                                title="Delete conversation"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Hover Accent Line */}
                      <div className={`absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-teal-500 transform transition-transform duration-200 ${hoveredConversation === conversation.id ? 'scale-x-100' : 'scale-x-0'}`}></div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bottom Fade Gradient Indicator */}
              {!isScrolledBottom && conversations.length > 3 && (
                <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none z-10 flex items-end justify-center pb-3">
                  <div className="flex items-center gap-2 text-gray-500 text-sm font-medium bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm">
                    <ChevronDown className="w-4 h-4 animate-bounce" />
                    <span>More conversations below</span>
                  </div>
                </div>
              )}

              {/* Scroll to Top Button */}
              {showScrollTop && (
                <button
                  onClick={scrollToTop}
                  className="absolute bottom-4 right-4 w-12 h-12 bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 z-20"
                  title="Scroll to top"
                >
                  <ArrowUp className="w-5 h-5" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* About Marta Card */}
        <div className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">About Marta</h3>
          <p className="text-gray-700 mb-4">
            Marta is your dedicated AI Career Coach, combining 15+ years of career coaching expertise with deep insights into your unique professional journey. She knows your resume, achievements, skills, and aspirations inside and out, enabling her to provide personalized guidance that's tailored specifically to you.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900">Context-Aware</p>
                <p className="text-sm text-gray-600">References your actual experiences and achievements</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900">Action-Oriented</p>
                <p className="text-sm text-gray-600">Provides concrete next steps and strategies</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900">Supportive Mentor</p>
                <p className="text-sm text-gray-600">Warm, empathetic, and always in your corner</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Start Cards */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <MessageCircle className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Ask Questions</h3>
            <p className="text-gray-600 mb-4">
              Get instant answers about your career development, skills, and goals
            </p>
            <Button
              variant="outline"
              onClick={handleNewConversation}
              className="w-full"
            >
              Chat with Marta
            </Button>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
              <Sparkles className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Get Guidance</h3>
            <p className="text-gray-600 mb-4">
              Receive personalized advice based on your complete career profile
            </p>
            <Button
              variant="outline"
              onClick={handleNewConversation}
              className="w-full"
            >
              Get Advice
            </Button>
          </div>
        </div>
      </div>

      {/* Chat Modal */}
      {showChat && (
        <AICoachChat
          isOpen={showChat}
          onClose={handleCloseChat}
          isModal={true}
          conversationId={selectedConversation}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Conversation"
        message="Are you sure you want to delete this conversation? This action cannot be undone and all messages will be permanently removed."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
      </div>
    </FeatureGate>
    </SetupFeeGate>
  );
};

export default AICoach;
